/**
 * `<NotebooksTreePane>` — the Editor's left pane (UX-reconciliation F4.1; ported from
 * the standalone's three-pane editor composition).
 *
 * Renders the notebooks tree lazily — root nodes load on mount, each child level
 * loads when its parent is expanded. Matches the backend's
 * `NotebookRepo.list_children` "walk this level by level" pattern.
 *
 * **Expansion fetches both child notebooks AND notes inside the notebook** —
 * matches the standalone's tree where a folder expands to reveal both sub-folders
 * and the notes it contains. Notes are leaf rows; clicking a note navigates the
 * editor to `/editor/<noteId>` (toEditor builder).
 *
 * The standalone also shows an "All notes" and "Trash" entry; in v1 we ship only
 * the tree (the trash recovery surface lives under Settings → Corpus Health; the
 * all-notes view is a saved search / Search query).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { Icon, AutoroutedBadge } from '@/components';
import { LoadingState, InlineError, usePromptModal, useConfirmModal } from '@/components';
import {
  getNote,
  noteTitles,
  updateNote,
  createNote,
  deleteNote as apiDeleteNote,
  listNotebooks,
} from '@/api';
import {
  useNoteQuery,
  prefetchNote,
  useNotebookAncestorsQuery,
  useNotebookChildrenQuery,
  useNotebookNotesQuery,
  useCreateNotebookMutation,
  useRenameNotebookMutation,
  useDeleteNotebookMutation,
  useReparentNotebookMutation,
  useArchiveNotebookMutation,
  useMoveNoteMutation,
  useSetNotebookAutorouterMutation,
  toErrorEnvelope,
} from '@/hooks';
import { ROUTES, toEditor } from '@/routes';
import {
  useUiStore,
  useHighlightTargetStore,
  HIGHLIGHT_FRESHNESS_MS,
} from '@/stores';
import { asNoteId } from '@/types';
import type { NoteSummary, NotebookId, NotebookItem, NotebookSummary, NoteTitleItem } from '@/types';
import type { NotebookSortMode } from '@/stores';
import { NoteDetailsModal } from './NoteDetailsModal';
import { nextNewNoteTitle } from './newNoteName';
import { TrashSection } from './TrashSection';

/**
 * The per-workspace **system Inbox** notebook. Identified by name — a top-level notebook titled
 * exactly this (matches the backend's `INBOX_NOTEBOOK_NAME`). Inbound mail + captures land here.
 * It is pinned in its own section at the top of the tree, and its rename/delete actions are
 * hidden (the backend also refuses to rename/delete it).
 */
const INBOX_NOTEBOOK_TITLE = 'Inbox';

function isInboxNotebook(nb: NotebookItem): boolean {
  return nb.parentId === null && nb.title === INBOX_NOTEBOOK_TITLE;
}

/**
 * The protected system Archive (2026-06-12) — root-level notebook with this exact title.
 * Archived folders are re-parented under it (right-click → Archive folder); it renders in
 * its own bottom section like Trash, and the backend refuses rename/move/delete on it.
 */
const ARCHIVE_NOTEBOOK_TITLE = 'Archive';

function isArchiveNotebook(nb: NotebookItem): boolean {
  return nb.parentId === null && nb.title === ARCHIVE_NOTEBOOK_TITLE;
}

/** MM/DD (zero-padded) created date for a note row in the tree — unintrusive, left of the title. */
function formatNoteRowDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const d = new Date(t);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * `<RootDropZone>` — drag-and-drop target that re-parents the dragged notebook to the **top
 * level** (parent_id = null). Collapsed/invisible until a NOTEBOOK is being dragged (notes can't
 * live at the root of the tree), then it expands into a dashed "Move to top level" strip and
 * highlights on hover. Renders inside the tree's `<DndContext>`.
 */
function RootDropZone(): JSX.Element {
  const { setNodeRef, isOver, active } = useDroppable({ id: 'root', data: { type: 'root' } });
  const draggingNotebook = active?.data.current?.type === 'notebook';
  return (
    <div
      ref={setNodeRef}
      className={
        'notebooks-pane__root-drop' +
        (draggingNotebook ? ' is-armed' : '') +
        (isOver && draggingNotebook ? ' is-over' : '')
      }
      aria-hidden={!draggingNotebook}
    >
      Move to top level
    </div>
  );
}

/**
 * Month-name → calendar index (0-11). Recognizes full names and 3-letter
 * abbreviations, both case-insensitive, with optional surrounding whitespace.
 * Returns null for anything that isn't a month label.
 *
 * 2026-05-31 (Chris ask) — folders named after months should appear in
 * calendar order, not alphabetical. The comparator below routes through this
 * helper so a sibling group like [April, August, December, February, ...]
 * sorts to [January, February, March, ...] instead.
 */
const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];
const MONTH_ABBR = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];
function monthIndex(title: string | null | undefined): number | null {
  if (title == null) return null;
  const t = title.trim().toLowerCase();
  if (t.length === 0) return null;
  const full = MONTH_NAMES.indexOf(t);
  if (full >= 0) return full;
  const abbr = MONTH_ABBR.indexOf(t);
  if (abbr >= 0) return abbr;
  return null;
}

/**
 * Phase E (2026-05-20) — pure sort helper applied to notebook arrays and
 * note arrays alike (both have a `.title` string). Keeps the logic in one
 * place so the chevron dropdown's mode applies uniformly down the tree.
 * 'manual' returns the input order unchanged — placeholder for the future
 * drag-and-drop reorder.
 *
 * 2026-05-31: alpha modes now route month names through `monthIndex` so
 * Jan/Feb/Mar/.../Dec siblings appear in calendar order. Non-month siblings
 * keep the existing Intl.Collator alphabetical comparator. Month-vs-non-month
 * pairs compare by the literal title strings (the month name reads as if it
 * were an ordinary word — "January" lands before "Notes" alphabetically as
 * usual). The reverse mode flips both groups symmetrically.
 */
function sortByMode<T extends { title?: string | null; createdAt?: string | null }>(
  items: readonly T[],
  mode: NotebookSortMode,
): T[] {
  if (mode === 'manual') return [...items];
  const out = [...items];
  // created_* sorts by the note metadata creation date. Notebooks have no
  // createdAt (the field is absent on NotebookItem) — when none of the items
  // carry a date, fall through to the alpha comparator so notebooks stay in a
  // sensible order rather than collapsing to "all equal" (= input order).
  if (mode === 'created_desc' || mode === 'created_asc') {
    const hasDates = out.some((it) => typeof it.createdAt === 'string');
    if (hasDates) {
      // String compare on ISO-8601 timestamps is chronological.
      out.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
      if (mode === 'created_desc') out.reverse();
      return out;
    }
    // No dates → notebooks; fall back to alpha ascending (stable, sensible).
  }
  const cmp = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
  out.sort((a, b) => {
    const ai = monthIndex(a.title);
    const bi = monthIndex(b.title);
    // Both are month names → calendar order.
    if (ai !== null && bi !== null) return ai - bi;
    // Exactly one is a month — compare by literal title (no special handling
    // beyond the standard collator), so a month name slots into its normal
    // alphabetical position relative to non-month siblings.
    return cmp.compare(a.title ?? '', b.title ?? '');
  });
  if (mode === 'alpha_desc') out.reverse();
  return out;
}

/** Case-insensitive substring test used by the title filter (task 2). */
function titleMatches(title: string | null | undefined, filter: string): boolean {
  if (filter === '') return true;
  return (title ?? '').toLowerCase().includes(filter.toLowerCase());
}

/**
 * 2026-05-18 user story — duration of the incandescent title-glow pulse on
 * the note title after the active note changes. The keyframe (`incandescent-
 * title-pulse` in app.css) loops every 3 seconds with `iteration-count:
 * infinite`; this constant is the window the JS keeps the class applied
 * before tearing it off. 10s ≈ 3.5 full cycles — what the user asked for
 * (50% slower per cycle + one more pulse than the prior 2s × 2.5 cycles).
 */
const SELECTED_PULSE_DURATION_MS = 10_000;

/** Page size for the "All notes" flat list at the top of the tree. Generous —
 *  the virtualized list keeps render cost bounded at large counts. */
const ALL_NOTES_PAGE_LIMIT = 500;

/** Task 4 — drag-to-resize the pane. Persisted px width + clamp bounds. */
const PANE_WIDTH_KEY = 'aletheia.notebookPaneWidth';
const PANE_WIDTH_MIN = 180;
const PANE_WIDTH_MAX = 600;

function loadPaneWidth(): number | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(PANE_WIDTH_KEY);
  if (raw === null) return null;
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n)) return null;
  return Math.min(PANE_WIDTH_MAX, Math.max(PANE_WIDTH_MIN, n));
}

function savePaneWidth(px: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PANE_WIDTH_KEY, String(Math.round(px)));
  } catch {
    /* quota — ignore */
  }
}

interface NotebooksTreePaneProps {
  /** The notebook id currently in scope (highlighted in the tree). `null` for none. */
  activeNotebookId?: NotebookId | null;
  /** The note id currently open in the editor (highlighted in the tree). `null` for none. */
  activeNoteId?: string | null;
}

/**
 * 2026-05-18 — derive the "active notebook" from the currently-open note,
 * not just from the `?notebookId=` URL param. When a user navigates to a
 * note via search / chat / a backlink, the URL only carries `noteId`; the
 * note's containing notebook is in `note.notebookId`. Auto-expanding that
 * notebook is what makes the blue pulse + scroll on the note's row visible
 * (otherwise the row isn't in the DOM at all).
 *
 * The note fetch here is the same query EditorSurface runs — TanStack Query
 * dedupes on `[note, noteId, includeCompanion]`, so there's no extra request.
 */
function useEffectiveActiveNotebookId(
  fromUrl: NotebookId | null,
  activeNoteId: string | null,
): NotebookId | null {
  const noteQuery = useNoteQuery(
    activeNoteId !== null ? asNoteId(activeNoteId) : null,
    false,
  );
  // URL param always wins (the user explicitly navigated to a notebook).
  if (fromUrl !== null) return fromUrl;
  const fromNote = noteQuery.data?.note.notebookId as NotebookId | null | undefined;
  return fromNote ?? null;
}

/**
 * 2026-05-18 — set of notebook ids on the root-down path to the currently
 * active notebook (inclusive). When the user lands on a note in /A/B/C/note,
 * this Set contains {A, B, C}. NotebookNode auto-expands when its id is in
 * the set — this is the "user clicks search result, the full folder path
 * opens automatically" behaviour (was: only immediate parent expanded).
 *
 * Empty set when no active notebook (e.g. on /search or before the note
 * fetch returns). One round-trip to `/api/v1/notebooks/{id}/ancestors`,
 * cached aggressively (notebook hierarchy rarely changes).
 */
function useActiveAncestorSet(
  activeNotebookId: NotebookId | null,
): ReadonlySet<NotebookId> {
  const query = useNotebookAncestorsQuery(activeNotebookId);
  // useMemo would be marginally more efficient but the dep dance is finicky
  // (array identity vs content); a fresh Set per render is cheap enough
  // (notebook trees are small — tens to hundreds).
  const ids = query.data?.notebookIds ?? [];
  return new Set(ids);
}

export function NotebooksTreePane({
  activeNotebookId: activeNotebookIdProp = null,
  activeNoteId = null,
}: NotebooksTreePaneProps): JSX.Element {
  // 2026-05-18 — derive the effective active notebook from the active note's
  // notebookId when the URL didn't supply one. Drives NotebookNode's auto-
  // expand so the active note's row actually appears in the DOM (otherwise
  // the pulse class is applied to a row that isn't there, and the scroll
  // querySelector has nothing to find).
  const activeNotebookId = useEffectiveActiveNotebookId(
    activeNotebookIdProp,
    activeNoteId,
  );

  // 2026-05-18 — the full root-down ancestor chain for the active notebook.
  // Threaded through every NotebookLevel + NotebookNode so EVERY notebook
  // on the path (not just the immediate parent) auto-expands. Needed for
  // nested notebooks: a note in /A/B/C requires A, B, AND C expanded for
  // the row to render. Without this we only expanded C and the user still
  // had to manually open A and B.
  const activeAncestorIds = useActiveAncestorSet(activeNotebookId);

  // "My Notes" jumps to the editor with no note selected — the corpus overview page.
  const paneNavigate = useNavigate();

  // 2026-05-18 user story — track the active-note transition so we can fire
  // the 5s incandescent-blue pulse + scroll-into-view on the matching <NoteRow>.
  //
  // Two trigger conditions:
  //   1. `activeNoteId` transition while this pane stays mounted (user clicks
  //      a row in the tree, or a search result with the editor already open).
  //   2. Initial mount of this pane WHEN a fresh highlight target points at
  //      `activeNoteId` — i.e. the user just navigated here from Search or
  //      Chat. Without this case, the very first arrival from outside the
  //      editor surface would never pulse (prevActiveRef seeds to activeNoteId
  //      on mount, so condition 1 sees no transition).
  //
  // The page-refresh / direct-bookmark case is suppressed because there's no
  // fresh highlight target — the pulse stays a navigation affordance, not a
  // "you-are-here" badge.
  // 2026-05-18 — subscribe to the highlight store directly (was a snapshot
  // read inside an `[activeNoteId]`-only effect). The snapshot approach
  // missed two real cases:
  //   1. User clicks a search result for the note they're ALREADY on —
  //      activeNoteId doesn't change, the effect doesn't re-run, no pulse.
  //   2. The store target arrives just after the effect ran (timing race
  //      between click handler + navigate's re-render).
  // With a subscription the effect re-runs on EITHER deps change, covering
  // both cases. The EditorSurface effect that clears the target will trigger
  // ANOTHER re-run of this effect (target = null), but the early-return
  // path handles that — and the in-flight setTimeout for pulseNoteId is
  // independent, so a stale clear doesn't interrupt a pulse mid-cycle.
  const highlightTarget = useHighlightTargetStore((s) => s.target);
  const prevActiveRef = useRef<string | null>(activeNoteId);
  const lastPulseTargetRef = useRef<string | null>(null);
  const [pulseNoteId, setPulseNoteId] = useState<string | null>(null);
  useEffect(() => {
    if (activeNoteId === null) {
      prevActiveRef.current = activeNoteId;
      return;
    }
    const isTransition =
      prevActiveRef.current !== null && prevActiveRef.current !== activeNoteId;
    const isFreshHighlightForThisNote =
      highlightTarget !== null &&
      highlightTarget.noteId === activeNoteId &&
      Date.now() - highlightTarget.requestedAt < HIGHLIGHT_FRESHNESS_MS;
    // Diagnostic — enable via `localStorage.debug = 'highlight'` in DevTools.
    if (typeof window !== 'undefined' && window.localStorage?.getItem('debug') === 'highlight') {
      // eslint-disable-next-line no-console
      console.debug('[highlight] NotebooksTreePane.effect', {
        activeNoteId,
        prev: prevActiveRef.current,
        isTransition,
        target: highlightTarget,
        isFreshHighlightForThisNote,
        lastPulseTarget: lastPulseTargetRef.current,
        willPulse:
          (isTransition || isFreshHighlightForThisNote) &&
          // De-dupe: don't re-pulse the same target that we already fired for
          lastPulseTargetRef.current !== `${activeNoteId}#${highlightTarget?.requestedAt ?? 0}`,
      });
    }
    if (!isTransition && !isFreshHighlightForThisNote) {
      prevActiveRef.current = activeNoteId;
      return;
    }
    // De-dupe: if THIS exact target+activeNoteId already fired a pulse, skip.
    // Prevents a re-render storm (e.g. the EditorSurface clear-effect triggering
    // a same-render re-run) from re-stamping pulseNoteId and resetting the timer.
    const pulseKey = `${activeNoteId}#${highlightTarget?.requestedAt ?? 0}`;
    if (lastPulseTargetRef.current === pulseKey) {
      prevActiveRef.current = activeNoteId;
      return;
    }
    lastPulseTargetRef.current = pulseKey;
    setPulseNoteId(activeNoteId);
    prevActiveRef.current = activeNoteId;
    const handle = window.setTimeout(() => {
      setPulseNoteId((current) => (current === activeNoteId ? null : current));
    }, SELECTED_PULSE_DURATION_MS);
    return () => window.clearTimeout(handle);
  }, [activeNoteId, highlightTarget]);

  // Scroll the pulsing row to centre. Centralised here (not per-row) because:
  //  (a) the same noteId can appear twice — pinned at the top AND under a
  //      notebook — and we want a single, deterministic scroll target;
  //  (b) per-row refs add a useRef + useEffect to every row in the tree, vs
  //      one effect + one querySelector here.
  //
  // 2026-05-18 — when the user arrives via a search result or chat citation,
  // the parent notebook may have been COLLAPSED at click time. The auto-
  // expand effect in NotebookNode (added the same day) eventually opens it,
  // but the notes inside the newly-expanded notebook still need to fetch
  // (useNotebookNotesQuery is gated on `enabled: expanded`). So at this
  // effect's first run the row probably ISN'T in the DOM. Strategy:
  //   1. Try once on rAF — fast path for rows already rendered.
  //   2. If not found, install a MutationObserver on the tree. When the row
  //      appears (notebook expands + notes fetch + NoteRow renders), scroll.
  //   3. Give up after the pulse-duration elapses + a small buffer.
  useEffect(() => {
    if (pulseNoteId === null) return;
    const tree = document.querySelector(
      '.notebooks-pane__tree',
    ) as HTMLElement | null;
    if (tree === null) return;

    const tryScroll = (): boolean => {
      const treeRow = tree.querySelector(
        `[data-note-id="${pulseNoteId}"]:not(.sb-pin-group [data-note-id])`,
      ) as HTMLElement | null;
      const pinnedRow = tree.querySelector(
        `[data-note-id="${pulseNoteId}"]`,
      ) as HTMLElement | null;
      const target = treeRow ?? pinnedRow;
      if (target === null) return false;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    };

    // Cleanup handles populated as resources are created. The returned
    // teardown reads them at unmount/dep-change time; whatever was assigned
    // by then gets cleared.
    let rafHandle: number | null = null;
    let observer: MutationObserver | null = null;
    let timeoutHandle: number | null = null;

    rafHandle = requestAnimationFrame(() => {
      rafHandle = null;
      if (tryScroll()) return;
      // Row not yet rendered (parent notebook still expanding / fetching).
      // Watch for it.
      observer = new MutationObserver(() => {
        if (tryScroll()) {
          observer?.disconnect();
          observer = null;
          if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      });
      observer.observe(tree, { childList: true, subtree: true });
      // Give up after the pulse duration + a small buffer; if we haven't
      // found the row in 5s, either the note isn't in this tree at all,
      // or the user's already navigated past it.
      timeoutHandle = window.setTimeout(() => {
        observer?.disconnect();
        observer = null;
        timeoutHandle = null;
      }, SELECTED_PULSE_DURATION_MS);
    });

    return () => {
      if (rafHandle !== null) cancelAnimationFrame(rafHandle);
      observer?.disconnect();
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
    };
  }, [pulseNoteId]);

  // Task 2 — title filter. Lifted here so it threads down through every level.
  // Empty string = current (unfiltered) behaviour.
  const [filter, setFilter] = useState('');

  // Task 4 — drag-to-resize. The notebooks pane is the FIRST grid column of the
  // app shell (`.app-shell--notebooks-left { grid-template-columns: var(--sidebar-w) 1fr }`),
  // so the column width is the `--sidebar-w` custom property — NOT the aside's own
  // `width`. Setting `width` on the aside (a grid item) can't move the track; it just
  // overflows/underflows it (the original resize bug). So the drag drives `--sidebar-w`
  // on the shell, which actually resizes the column. Persisted px width + clamp bounds.
  const asideRef = useRef<HTMLElement>(null);
  const applyPaneWidth = useCallback((px: number): void => {
    asideRef.current?.closest<HTMLElement>('.app-shell')?.style.setProperty(
      '--sidebar-w',
      `${px}px`,
    );
  }, []);
  // Restore the persisted width on mount (writes the shell custom property).
  useEffect(() => {
    const w = loadPaneWidth();
    if (w !== null) applyPaneWidth(w);
  }, [applyPaneWidth]);
  const onResizeStart = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = asideRef.current?.getBoundingClientRect().width ?? PANE_WIDTH_MIN;
      let lastW = startW;
      function onMove(ev: MouseEvent): void {
        lastW = Math.min(
          PANE_WIDTH_MAX,
          Math.max(PANE_WIDTH_MIN, startW + (ev.clientX - startX)),
        );
        applyPaneWidth(lastW);
      }
      function onUp(): void {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        savePaneWidth(lastW); // already clamped in onMove
      }
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [applyPaneWidth],
  );

  // Task 6 — Arrow Up / Down move focus across visible rows in render order.
  // We query the DOM for `.notebooks-pane__row` (every notebook + note + pinned
  // row carries this class and a tabIndex), find the active element's index,
  // and move focus to the neighbour. Robust to the lazy tree (only rendered
  // rows participate).
  const onTreeKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const root = asideRef.current;
    if (root === null) return;
    const rows = Array.from(
      root.querySelectorAll<HTMLElement>('.notebooks-pane__row'),
    );
    if (rows.length === 0) return;
    e.preventDefault();
    const active = document.activeElement as HTMLElement | null;
    const idx = active !== null ? rows.indexOf(active) : -1;
    let nextIdx: number;
    if (idx === -1) {
      // Focus isn't on a row yet — enter at the first (Down) or last (Up).
      nextIdx = e.key === 'ArrowDown' ? 0 : rows.length - 1;
    } else {
      nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
      nextIdx = Math.min(rows.length - 1, Math.max(0, nextIdx));
    }
    rows[nextIdx]?.focus();
  }, []);

  // The system Inbox notebook, if it exists yet (it's created on the first inbound capture —
  // email-to-inbox, etc.). Pinned in its own section at the top and excluded from the regular
  // root level below so it renders exactly once. Shares the `__root__` children query (deduped).
  const rootNotebooksQuery = useNotebookChildrenQuery(null);
  const inboxNotebook = rootNotebooksQuery.data?.notebooks.find(isInboxNotebook) ?? null;
  // The system Archive, if it exists yet (created on the first "Archive folder" action).
  // Pinned in its own bottom section like Trash; excluded from the regular root level.
  const archiveNotebook = rootNotebooksQuery.data?.notebooks.find(isArchiveNotebook) ?? null;

  // --- Drag-and-drop (dnd-kit): move notes between folders + reparent folders ----------------
  // PointerSensor with a small activation distance so a plain click still navigates — a drag
  // only begins once the pointer moves ~6px.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const reparentNotebookMutation = useReparentNotebookMutation();
  const moveNoteMutation = useMoveNoteMutation();
  const onDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event;
      if (over === null) return;
      const a = active.data.current as
        | { type?: string; id?: string; parentId?: string | null }
        | undefined;
      const o = over.data.current as { type?: string; id?: string } | undefined;
      if (a === undefined || o === undefined) return;
      const targetParentId: NotebookId | null =
        o.type === 'root' ? null : ((o.id as NotebookId | undefined) ?? null);
      if (a.type === 'note') {
        // A note can only move INTO a folder — a root note would vanish from the tree.
        if (o.type !== 'notebook' || targetParentId === null || a.id === undefined) return;
        moveNoteMutation.mutate({ noteId: asNoteId(a.id), notebookId: targetParentId });
      } else if (a.type === 'notebook' && a.id !== undefined) {
        if ((a.id as NotebookId) === targetParentId) return; // dropped onto itself
        if ((a.parentId ?? null) === targetParentId) return; // already there — no-op
        // Deeper cycles (drop into own descendant) are rejected server-side.
        reparentNotebookMutation.mutate({
          notebookId: a.id as NotebookId,
          parentId: targetParentId,
        });
      }
    },
    [moveNoteMutation, reparentNotebookMutation],
  );

  // Dual-classed: `.notebooks-pane*` legacy + standalone `.ed-pane.ed-notebooks` chrome.
  return (
    <aside
      ref={asideRef}
      className="notebooks-pane ed-pane ed-notebooks"
      aria-label="Notebooks"
      onKeyDown={onTreeKeyDown}
    >
      <DndContext sensors={dndSensors} onDragEnd={onDragEnd}>
      <div className="notebooks-pane__head ed-pane-head">
        <span className="notebooks-pane__head-label label-mono">Notebooks</span>
        <div className="notebooks-pane__head-actions">
          <NewNotebookButton parentId={null} />
          <NotebookSortControl />
        </div>
        <input
          type="text"
          className="notebooks-pane__filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          // U7: short placeholder — "Filter by title…" clipped to "Filt…" at narrow
          // pane widths. The aria-label + title keep the full explanation.
          placeholder="Filter"
          title="Filter notebooks and notes by title"
          aria-label="Filter notebooks and notes by title"
        />
      </div>
      <div className="notebooks-pane__tree ed-nb-list">
        {/* "My Notes" — the corpus overview page (the editor's no-note state). Active whenever
            no note is selected (incl. when a folder is selected). */}
        <button
          type="button"
          className={
            'notebooks-pane__mynotes' + (activeNoteId === null ? ' is-active' : '')
          }
          onClick={() => paneNavigate(ROUTES.editor)}
          title="Open your notes overview"
        >
          <Icon name="dashboard" size={14} />
          <span>My Notes</span>
        </button>
        {/* Drag-and-drop: a drop zone (visible only while dragging a folder) that re-parents
            the dragged notebook to the top level. */}
        <RootDropZone />
        {/* v4 P5 (GAP_ANALYSIS.md §X3) — Pinned section pins to the top of
            the tree. Reads pinnedNoteIds from useUiStore + resolves titles
            from the same noteTitles query. Empty hint when nothing is
            pinned. */}
        {/* The system Inbox, pinned in its own section at the very top. Renders nothing until
            the Inbox notebook exists (it's created on the first inbound capture). */}
        <InboxSection
          inbox={inboxNotebook}
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />

        <PinnedSection
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />

        {/* 2026-05-18 — the "All notes" flat-list section was REMOVED at the
            user's request: everything should be in folders. The legacy
            <AllNotesSection> covered (a) corpora imported without notebook
            structure, and (b) "I just want a flat list" navigation. Both
            use cases are deferred:
              - For (a), the import flow needs to ensure notes always land
                in a real notebook (currently Joplin bulk-import flattens
                folders into `notebook:Path/...` tags — separate fix).
              - For (b), the Search surface is the flat-list affordance.
            If a corpus has notes WITHOUT a notebook_id, they're invisible
            here — they're still findable via Search and the API. */}
        <NotebookLevel
          parentId={null}
          depth={0}
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />

        {/* The system Archive (2026-06-12) — its own bottom section, like Trash. Renders
            only once a folder has been archived (the Archive notebook is created on first
            use). A full NotebookNode (expand/browse/drag notes out), but flagged isArchive
            so Rename/Delete/Archive are hidden — the backend refuses them too. Inside the
            DndContext: dragging a folder ONTO the Archive row archives it. */}
        <ArchiveSection
          archive={archiveNotebook}
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />

        {/* 2026-05-29 friction-log fix (#90) — the Trash folder is pinned at the
            BOTTOM of the tree. Acts visually like a folder (collapsible, trash icon,
            count badge) but is backed by `useSoftDeletedQuery` rather than a real
            notebook id. Right-click → Empty Trash; per-note right-click → Restore |
            Delete Permanently. Intentionally OUTSIDE the DndContext children loop —
            you can't drag the Trash itself, and dropping into Trash should remain a
            soft-delete (the Delete button on a note already does that). */}
        <TrashSection />
      </div>
      </DndContext>
      {/* Task 4 — drag handle on the right edge. CSS owns the visual; this is
          just the hit target + drag wiring. */}
      <div
        className="notebooks-pane__resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize notebooks pane"
        onMouseDown={onResizeStart}
      />
    </aside>
  );
}

/**
 * `<NotebookSortControl>` — Phase E (2026-05-20). The pane-head dropdown
 * that picks the global notebook + note sort mode. State lives in
 * useUiStore (localStorage-persisted), so the choice survives reload and
 * applies to every NotebookLevel + NotebookNode through the same hook.
 *
 * Three modes:
 *   alpha_asc  — A → Z (default)
 *   alpha_desc — Z → A
 *   manual     — backend's returned order, the placeholder for the future
 *                drag-and-drop reorder phase.
 */
function NotebookSortControl(): JSX.Element {
  const sortMode = useUiStore((s) => s.notebookSort);
  const setSortMode = useUiStore((s) => s.setNotebookSort);
  return (
    <select
      className="notebooks-pane__sort"
      value={sortMode}
      onChange={(e) => setSortMode(e.target.value as typeof sortMode)}
      aria-label="Sort notebooks and notes"
      title="Sort mode"
    >
      <option value="alpha_asc">A → Z</option>
      <option value="alpha_desc">Z → A</option>
      <option value="created_desc">Newest</option>
      <option value="created_asc">Oldest</option>
      <option value="manual">Manual</option>
    </select>
  );
}

/**
 * `<NewNotebookButton>` — Phase E.2 (2026-05-20). Creates a new notebook as
 * a child of `parentId` (null for a root). Uses `window.prompt()` for the
 * title input — minimal, no inline editor; the form-mode UX is a future
 * polish. Re-fetches the affected level on success so the new notebook
 * appears immediately.
 */
function NewNotebookButton({ parentId }: { parentId: NotebookId | null }): JSX.Element {
  const create = useCreateNotebookMutation();
  const expand = useUiStore((s) => s.expandNotebook);
  const { modal, prompt } = usePromptModal();

  async function onClick(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    const isRoot = parentId === null;
    const title = await prompt({
      eyebrow: isRoot ? 'New notebook' : 'New subfolder',
      title: isRoot ? 'Name your notebook' : 'Name your subfolder',
      placeholder: isRoot ? 'Untitled notebook' : 'Untitled subfolder',
      confirmLabel: 'Create',
    });
    if (title === null) return;
    create.mutate(
      { title, parentId },
      {
        onSuccess: () => {
          // Auto-expand the parent so the user sees the new child appear
          // in place. Top-level creates always show; subfolder creates
          // need the parent to be open.
          if (parentId !== null) expand(parentId);
        },
      },
    );
  }

  const label = parentId === null ? 'New notebook' : 'New subfolder';
  // Root-level button gets a visible "New" text label so it doesn't read as
  // an unlabelled "+" glyph; nested/subfolder buttons stay icon-only.
  const showText = parentId === null;
  return (
    <>
      {modal}
      <button
        type="button"
        className="notebooks-pane__new"
        onClick={(e) => void onClick(e)}
        disabled={create.isPending}
        aria-label={label}
        title={label}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        {showText && <span className="notebooks-pane__new-label">New</span>}
      </button>
    </>
  );
}

/**
 * `<AutorouteFolderDot>` — the per-folder Autorouter permission indicator
 * (docs/design/AUTOROUTER-SPEC.md §3.2 / decision #10). A small dot that GLOWS while
 * autorouting is permitted for this folder; clicking toggles it. When disabled, the router
 * can't file into / rename within / create children under the folder — humans + MCP are
 * unaffected.
 */
function AutorouteFolderDot({
  notebookId,
  enabled,
}: {
  notebookId: NotebookId;
  enabled: boolean;
}): JSX.Element {
  const toggle = useSetNotebookAutorouterMutation();
  const { modal, confirm } = useConfirmModal();
  return (
    <>
      {modal}
      <button
        type="button"
        className={'nb-autoroute-dot' + (enabled ? ' is-on' : '')}
        title={
          enabled
            ? 'Autorouting ON — the AI may file notes into this folder automatically. Click to disable.'
            : 'Autorouting OFF — the AI will not file notes into this folder. Click to enable.'
        }
        aria-label={enabled ? 'Disable autorouting for this folder' : 'Enable autorouting'}
        aria-pressed={enabled}
        disabled={toggle.isPending}
        onClick={async (e) => {
          e.stopPropagation();
          // Confirm before flipping the folder's router permission — the destructive
          // (disable) direction carries the message Chris specified; enabling is the benign
          // parallel.
          const ok = await confirm(
            enabled
              ? {
                  eyebrow: 'Autorouter',
                  title: 'Disable autorouter for this folder?',
                  message:
                    'This will prevent the AI from adding notes here automatically.',
                  confirmLabel: 'Disable',
                  destructive: true,
                }
              : {
                  eyebrow: 'Autorouter',
                  title: 'Enable autorouter for this folder?',
                  message: 'The AI will be able to file notes into this folder automatically.',
                  confirmLabel: 'Enable',
                },
          );
          if (!ok) return;
          toggle.mutate({ notebookId, enabled: !enabled });
        }}
      />
    </>
  );
}

/**
 * `<NotebookRowMenu>` — Phase E.2 per-row action menu. Hover-revealed
 * "⋯" button on each notebook row that opens a popover with three
 * actions: New subfolder, Rename, Delete. Uses the same click-outside +
 * Escape pattern as the BurgerMenu in the TopBar.
 */
function NotebookRowMenu({
  notebook,
  isInbox = false,
  onRename,
}: {
  notebook: NotebookItem;
  isInbox?: boolean;
  /** Begin the row's INLINE rename editor (2026-06-10 — replaces the old modal prompt). */
  onRename?: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const create = useCreateNotebookMutation();
  const del = useDeleteNotebookMutation();
  const expand = useUiStore((s) => s.expandNotebook);
  const { modal: promptModal, prompt } = usePromptModal();
  const { modal: confirmModal, confirm } = useConfirmModal();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function onNewSubfolder(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    setOpen(false);
    const title = await prompt({
      eyebrow: 'New subfolder',
      title: `Add a subfolder under "${notebook.title}"`,
      placeholder: 'Untitled subfolder',
      confirmLabel: 'Create',
    });
    if (title === null) return;
    create.mutate(
      { title, parentId: notebook.id },
      { onSuccess: () => expand(notebook.id) },
    );
  }

  function onRenameClick(e: React.MouseEvent): void {
    e.stopPropagation();
    setOpen(false);
    onRename?.(); // the row swaps its title for the inline editor
  }

  async function onDelete(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    setOpen(false);
    const ok = await confirm({
      eyebrow: 'Delete notebook',
      title: `Delete "${notebook.title}"?`,
      message:
        'Notes inside the notebook are preserved — they stay assigned and can be recovered from the Trash folder at the bottom of the notebook tree.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      destructive: true,
    });
    if (!ok) return;
    del.mutate(notebook.id);
  }

  return (
    <>
      {promptModal}
      {confirmModal}
      <div className="notebooks-pane__row-menu" ref={ref}>
        <button
          type="button"
          className="notebooks-pane__row-menu-btn"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Actions for ${notebook.title}`}
          title="Actions"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
            <circle cx="3" cy="7" r="1.2" />
            <circle cx="7" cy="7" r="1.2" />
            <circle cx="11" cy="7" r="1.2" />
          </svg>
        </button>
        {open && (
          <div className="notebooks-pane__row-menu-popover" role="menu">
            <button
              type="button"
              role="menuitem"
              className="notebooks-pane__row-menu-item"
              onClick={(e) => void onNewSubfolder(e)}
              disabled={create.isPending}
            >
              New subfolder
            </button>
            {!isInbox && (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="notebooks-pane__row-menu-item"
                  onClick={onRenameClick}
                >
                  Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="notebooks-pane__row-menu-item notebooks-pane__row-menu-item--delete"
                  onClick={(e) => void onDelete(e)}
                  disabled={del.isPending}
                >
                  Delete…
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Phase E.4 (2026-05-20) — right-click context menu primitive. One item
 * per action; ``danger`` paints the row red on hover. Position is fixed
 * to the cursor coordinates, viewport-clamped so the menu never renders
 * off-screen (and so a click near the bottom-right of the viewport still
 * shows the whole menu by flipping above / to the left).
 *
 * Closes on outside-click, Escape, blur, or after any item is invoked.
 */
interface RowContextMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface RowContextMenuProps {
  /** Cursor position in viewport coords. null = closed. */
  position: { x: number; y: number } | null;
  items: RowContextMenuItem[];
  onClose: () => void;
}

function RowContextMenu({ position, items, onClose }: RowContextMenuProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (position === null) return;
    function onMouseDown(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    function onScroll(): void {
      onClose();
    }
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    // Scroll while a context menu is open is a UX edge — close it so the
    // menu doesn't float over stale content. Capture phase to catch
    // scrolls in any container.
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [position, onClose]);

  if (position === null) return null;

  // Viewport clamp — flip the menu left / up if it would render outside.
  // Numbers are conservative; menu is small (~180×100px typically).
  const MENU_W = 200;
  const MENU_H = 36 * items.length + 16;
  const x =
    position.x + MENU_W > window.innerWidth - 8
      ? Math.max(8, position.x - MENU_W)
      : position.x;
  const y =
    position.y + MENU_H > window.innerHeight - 8
      ? Math.max(8, position.y - MENU_H)
      : position.y;

  return (
    <div
      ref={ref}
      role="menu"
      className="notebooks-pane__ctx-menu"
      style={{ position: 'fixed', left: x, top: y, minWidth: MENU_W }}
    >
      {items.map((item, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            item.onClick();
          }}
          className={
            'notebooks-pane__ctx-item' +
            (item.danger ? ' notebooks-pane__ctx-item--danger' : '')
          }
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Phase E.4 — react hook that encapsulates "open at cursor" state for
 * right-click context menus. Returns ``{ position, open, close }``.
 * The consumer wires ``open`` to its row's ``onContextMenu`` handler
 * and passes ``position`` to `<RowContextMenu>`.
 */
function useContextMenuPosition(): {
  position: { x: number; y: number } | null;
  open: (e: React.MouseEvent) => void;
  close: () => void;
} {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const open = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPosition({ x: e.clientX, y: e.clientY });
  }, []);
  const close = useCallback(() => setPosition(null), []);
  return { position, open, close };
}

/**
 * `<PinnedSection>` — v4 P5 (GAP_ANALYSIS.md §X3). Renders the user's
 * pinned notes at the very top of the sidebar so in-flight work is one
 * click away regardless of where the active note lives in the tree.
 *
 * Data: pinnedNoteIds from useUiStore (localStorage-backed, capped at 50);
 * titles resolved from the same `noteTitles` query AllNotesSection runs.
 * The pin-toggle affordance itself (a pin button on note rows) lands in a
 * follow-up — for v1, the section renders even when empty per the
 * skeleton-before-data rule so the user knows pinning IS a thing.
 */
function PinnedSection({
  activeNotebookId,
  activeNoteId,
  pulseNoteId,
  activeAncestorIds,
  filter,
}: {
  activeNotebookId: NotebookId | null;
  activeNoteId: string | null;
  pulseNoteId: string | null;
  activeAncestorIds: ReadonlySet<NotebookId>;
  filter: string;
}): JSX.Element {
  const pinnedIds = useUiStore((s) => s.pinnedNoteIds);
  const pinnedNotebookIds = useUiStore((s) => s.pinnedNotebookIds);
  const navigate = useNavigate();

  // Reuse the cached noteTitles query so we don't re-fetch just for the pins.
  // AllNotesSection runs the same query — React Query dedupes on the key.
  const query = useQuery({
    queryKey: ['editor.tree.all_notes', ALL_NOTES_PAGE_LIMIT],
    queryFn: () => noteTitles({ limit: ALL_NOTES_PAGE_LIMIT }),
  });
  const items: NoteTitleItem[] = query.data?.data.items ?? [];
  const titleById = new Map(items.map((it) => [it.noteId, it.title]));

  // Pinned FOLDERS render as full, real notebook nodes (moved out of the tree), so we need the
  // complete NotebookItem for each. The flat list_notebooks query has every notebook's
  // id/title/parentId; hasChildren is derived from the parentId links (a notebook is a parent
  // iff something points at it). Dedupes on the same query key other surfaces use.
  const notebooksQuery = useQuery({
    queryKey: ['list_notebooks', {}],
    queryFn: () => listNotebooks({}),
  });
  const allNotebooks: NotebookSummary[] = notebooksQuery.data?.data.items ?? [];
  const notebookById = new Map(allNotebooks.map((nb) => [nb.notebookId, nb]));
  const parentIdsWithChildren = new Set(
    allNotebooks.map((nb) => nb.parentId).filter((p): p is NotebookId => p != null),
  );

  // Render pinned IDs in user-pinned order (newest pin first). Drop any
  // pinned IDs that don't resolve to a known note (deleted notes, etc).
  const pinned = pinnedIds
    .map((id) => ({ id, title: titleById.get(asNoteId(id)) }))
    .filter((p): p is { id: string; title: string } => p.title !== undefined);

  // Pinned notebooks → full NotebookItem (in pin order). Drop any that no longer exist, plus the
  // system Inbox (it already has its own top section, so it never doubles even if pinned).
  // 2026-05-31: NotebookSummary.noteCount here is the DIRECT count from the
  // `list_notebooks` MCP tool, not the recursive count served by the new
  // `/notebooks/children` endpoint. Pinned rows therefore show direct-only
  // until/unless that MCP tool is updated to aggregate descendants. Tracked
  // as a follow-up — most pinned notebooks are leaves anyway.
  const pinnedNotebooks: NotebookItem[] = pinnedNotebookIds
    .map((id) => notebookById.get(id as NotebookId))
    .filter((nb): nb is NotebookSummary => nb !== undefined)
    .map((nb) => ({
      id: nb.notebookId,
      title: nb.title,
      parentId: nb.parentId,
      hasChildren: parentIdsWithChildren.has(nb.notebookId),
      noteCount: nb.noteCount,
      // The list_notebooks MCP tool (NotebookSummary) doesn't carry the autorouter flag;
      // the pinned section is a convenience duplicate, so default to enabled. The canonical
      // tree row shows the real value, and the dot's toggle writes to the real notebook id.
      autorouterEnabled: true,
    }))
    .filter((nb) => !isInboxNotebook(nb));

  const isEmpty = pinned.length === 0 && pinnedNotebooks.length === 0;

  return (
    <div className="notebooks-pane__pinned">
      <div className="sb-pin-head">Pinned</div>
      {isEmpty ? (
        <div
          className="notebooks-pane__empty"
          style={{ paddingLeft: 14, fontSize: 11.5, fontStyle: 'italic' }}
        >
          Nothing pinned yet.
        </div>
      ) : (
        <>
          {/* Pinned FOLDERS render as full, expandable notebook nodes — same ops as the tree
              (expand, notes inside, New note/subfolder, Rename, Delete, Unpin). They're MOVED
              here: NotebookLevel filters pinned folders out of the regular tree at every level. */}
          {pinnedNotebooks.length > 0 && (
            <ul className="notebooks-pane__list">
              {pinnedNotebooks.map((nb) => (
                <NotebookNode
                  key={nb.id}
                  notebook={nb}
                  depth={0}
                  activeNotebookId={activeNotebookId}
                  activeNoteId={activeNoteId}
                  pulseNoteId={pulseNoteId}
                  activeAncestorIds={activeAncestorIds}
                  filter={filter}
                />
              ))}
            </ul>
          )}
          {/* Pinned NOTES stay quick-links — a note lives in a notebook, so it can't "move". */}
          {pinned.length > 0 && (
            <div className="sb-pin-group">
              {pinned.map((p) => (
                <div
                  key={p.id}
                  data-note-id={p.id}
                  className={
                    'notebooks-pane__row ed-nb-row ed-nb-file' +
                    (p.id === activeNoteId ? ' active notebooks-pane__row--active' : '') +
                    (p.id === pulseNoteId ? ' notebooks-pane__row--just-selected' : '')
                  }
                  onClick={() => navigate(toEditor(p.id as never))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(toEditor(p.id as never));
                    }
                  }}
                  title={p.title || 'Untitled note'}
                  style={{ paddingLeft: 14 }}
                >
                  <Icon name="star" size={12} className="notebooks-pane__icon" />
                  <span className="notebooks-pane__title">
                    {p.title || 'Untitled note'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * `<InboxSection>` — the system Inbox notebook, pinned in its own section at the very top of the
 * sidebar (above Pinned). Inbound mail + captures land here. Renders nothing until the Inbox
 * notebook exists (created on first inbound capture). The node is flagged `isInbox` so its
 * rename/delete actions are hidden; everything else (expand, notes, New note, Pin) is normal.
 */
function InboxSection({
  inbox,
  activeNotebookId,
  activeNoteId,
  pulseNoteId,
  activeAncestorIds,
  filter,
}: {
  inbox: NotebookItem | null;
  activeNotebookId: NotebookId | null;
  activeNoteId: string | null;
  pulseNoteId: string | null;
  activeAncestorIds: ReadonlySet<NotebookId>;
  filter: string;
}): JSX.Element | null {
  if (inbox === null) return null;
  return (
    <div className="notebooks-pane__inbox">
      <div className="sb-pin-head">Inbox</div>
      <ul className="notebooks-pane__list">
        <NotebookNode
          notebook={inbox}
          depth={0}
          isInbox
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />
      </ul>
    </div>
  );
}

// AllNotesSection removed 2026-05-18 per user request — "everything should be in
// folders". The PinnedSection still reuses the `editor.tree.all_notes` query key
// to resolve pinned-note titles; the query is fired on demand from there. If a
// future iteration brings the flat list back, the prior component is in git
// history at commit e4c4981.

/**
 * `<ArchiveSection>` — the system Archive notebook, pinned in its own section at the bottom of
 * the sidebar (above Trash), mirroring the Inbox section idiom. Renders nothing until the
 * Archive notebook exists (created on the first right-click → Archive folder action). The node
 * is flagged `isArchive` so its rename/delete/archive actions are hidden; everything else
 * (expand, browse, drag contents out) is normal.
 */
function ArchiveSection({
  archive,
  activeNotebookId,
  activeNoteId,
  pulseNoteId,
  activeAncestorIds,
  filter,
}: {
  archive: NotebookItem | null;
  activeNotebookId: NotebookId | null;
  activeNoteId: string | null;
  pulseNoteId: string | null;
  activeAncestorIds: ReadonlySet<NotebookId>;
  filter: string;
}): JSX.Element | null {
  if (archive === null) return null;
  return (
    <div className="notebooks-pane__archive">
      <div className="sb-pin-head">Archive</div>
      <ul className="notebooks-pane__list">
        <NotebookNode
          notebook={archive}
          depth={0}
          isArchive
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />
      </ul>
    </div>
  );
}

interface NotebookLevelProps {
  parentId: NotebookId | null;
  depth: number;
  activeNotebookId: NotebookId | null;
  activeNoteId: string | null;
  pulseNoteId: string | null;
  /** Root-down chain to the active notebook; any NotebookNode whose id is in
   *  this set auto-expands. Threaded down from <NotebooksTreePane>. */
  activeAncestorIds: ReadonlySet<NotebookId>;
  /** Task 2 — case-insensitive title filter. Empty = unfiltered. */
  filter: string;
}

/**
 * Render one level of the tree (the children of `parentId`). Each node may expand to
 * render the next level — that's the recursive case.
 */
function NotebookLevel({
  parentId,
  depth,
  activeNotebookId,
  activeNoteId,
  pulseNoteId,
  activeAncestorIds,
  filter,
}: NotebookLevelProps): JSX.Element {
  // IMPORTANT: every hook must be called UNCONDITIONALLY before any early
  // return — React tracks hooks by call order across renders. Phase E.2's
  // first cut put `useUiStore((s) => s.notebookSort)` AFTER the
  // isLoading/isError/empty-corpus early returns, which broke the hook
  // count between renders (React error #310: "Rendered more hooks than
  // during the previous render"). Read both hooks at the very top.
  const query = useNotebookChildrenQuery(parentId);
  // Phase E (2026-05-20) — apply the global sort mode from useUiStore.
  // Both notebooks AND notes inside them go through the same comparator
  // for visual consistency; the mode picker lives in <NotebooksTreePane>.
  const sortMode = useUiStore((s) => s.notebookSort);
  // Pinned folders (and the system Inbox) live in their own top sections, so they're filtered OUT
  // of the regular tree here — at EVERY level, so a pinned folder MOVES rather than showing twice.
  // Reading the live pin set means pin/unpin relocates the folder instantly.
  const pinnedNotebookIds = useUiStore((s) => s.pinnedNotebookIds);

  if (query.isLoading) {
    return (
      <div className="notebooks-pane__loading" style={{ paddingLeft: depth * 16 + 12 }}>
        <LoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="notebooks-pane__error" style={{ paddingLeft: depth * 16 + 12 }}>
        <InlineError
          error={toErrorEnvelope(query.error)}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }
  const notebooks = query.data?.notebooks ?? [];
  // depth === 0 + zero notebooks is the common case for fresh corpora and for
  // imports that didn't carry notebook structure (Joplin → synthetic
  // `notebook:Path/...` tags). With the AllNotesSection removed (2026-05-18),
  // a zero-notebook corpus shows an empty notebooks pane — the user is
  // expected to organize into folders, OR the import flow is expected to
  // create notebooks. Notes without a notebook_id are invisible here; they
  // remain findable via Search.
  if (notebooks.length === 0 && depth === 0) {
    return <></>;
  }
  const sortedNotebooks = sortByMode(notebooks, sortMode).filter(
    (nb) =>
      !isInboxNotebook(nb) && !isArchiveNotebook(nb) && !pinnedNotebookIds.includes(nb.id),
  );
  return (
    <ul className="notebooks-pane__list">
      {sortedNotebooks.map((nb) => (
        <NotebookNode
          key={nb.id}
          notebook={nb}
          depth={depth}
          activeNotebookId={activeNotebookId}
          activeNoteId={activeNoteId}
          pulseNoteId={pulseNoteId}
          activeAncestorIds={activeAncestorIds}
          filter={filter}
        />
      ))}
    </ul>
  );
}

interface NotebookNodeProps {
  notebook: NotebookItem;
  depth: number;
  /** When true, this is the protected system Inbox — its rename/delete actions are hidden. */
  isInbox?: boolean;
  /** When true, this is the protected system Archive — rename/delete/archive are hidden. */
  isArchive?: boolean;
  activeNotebookId: NotebookId | null;
  activeNoteId: string | null;
  pulseNoteId: string | null;
  activeAncestorIds: ReadonlySet<NotebookId>;
  /** Task 2 — case-insensitive title filter. Empty = unfiltered. */
  filter: string;
}

function NotebookNode({
  notebook,
  depth,
  isInbox = false,
  isArchive = false,
  activeNotebookId,
  activeNoteId,
  pulseNoteId,
  activeAncestorIds,
  filter,
}: NotebookNodeProps): JSX.Element {
  const isActive = notebook.id === activeNotebookId;
  // 2026-05-18 — auto-expand when this notebook is the active one OR is on
  // the root-down path to it. The ancestor-set covers nested notebooks: a
  // note in /A/B/C requires A, B, AND C expanded for the row to render.
  // `isActive` is kept as a separate check because the URL ?notebookId=
  // route doesn't always populate the ancestor query (the user clicked a
  // notebook directly, not a note inside it).
  //
  // Without auto-expand: when the parent notebook stays collapsed, the
  // NoteRow never renders, the pulse class has nothing to apply to, and
  // the scroll querySelector finds no element. With auto-expand including
  // the full ancestor chain, deeply nested notes (/A/B/C/note) open all
  // three ancestors at once.
  //
  // Seed the useState with the initial check so the very first render
  // already has expanded=true (vs. expanding on the next render after a
  // useEffect). The useEffect handles the changed-AFTER-mount case (user
  // navigates between notes in different folders).
  const isOnActivePath = isActive || activeAncestorIds.has(notebook.id);

  // Phase E (2026-05-20) — tristate expansion. The Phase-12 boolean Set
  // couldn't distinguish "user explicitly collapsed" from "no record yet,"
  // so the auto-expand-on-active-path effect would immediately revert any
  // manual collapse on a notebook containing the active note. The new
  // notebookExpansion map records 'collapsed' explicitly; the auto-expand
  // path checks for the EXACT value before overriding.
  const explicit = useUiStore((s) => s.notebookExpansion[notebook.id]);
  const expandNotebookAction = useUiStore((s) => s.expandNotebook);
  const collapseNotebookAction = useUiStore((s) => s.collapseNotebook);
  // Task 5 — pin / unpin this notebook via the row context menu.
  const isNotebookPinned = useUiStore((s) => s.isNotebookPinned);
  const pinNotebook = useUiStore((s) => s.pinNotebook);
  const unpinNotebook = useUiStore((s) => s.unpinNotebook);
  const notebookPinned = isNotebookPinned(notebook.id);
  // Sort mode read in this component too — the notes-within-notebook list
  // is rendered here, not inside NotebookLevel where sortMode is also read.
  const sortMode = useUiStore((s) => s.notebookSort);
  // Phase E.4 — right-click context menu (Rename / Delete) for this row.
  // The popover position is the cursor's coords; closes on outside click
  // or Escape. Shares the same mutation hooks as the ⋯ hover menu.
  const ctx = useContextMenuPosition();
  const renameMutation = useRenameNotebookMutation();
  const deleteMutation = useDeleteNotebookMutation();
  // UX fix #93 (2026-05-29) — right-click "New subfolder" creates a child under
  // this notebook. The header's "+" button only created root-level notebooks;
  // discoverable subfolder creation was missing.
  const createMutation = useCreateNotebookMutation();
  // 2026-06-12 — right-click "Archive folder": re-parents this folder under the system
  // Archive (created on first use), which renders in its own bottom section.
  const archiveMutation = useArchiveNotebookMutation();
  // E.4b — branded prompt/confirm modals so we never fall back to
  // window.prompt / window.confirm here either.
  const { modal: nbPromptModal, prompt: nbPrompt } = usePromptModal();
  const { modal: nbConfirmModal, confirm: nbConfirm } = useConfirmModal();
  // "New note here" creates a note immediately (no modal): auto-named `New note NN`,
  // created in this notebook, then we navigate to it for inline renaming + refresh the tree.
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [creatingNote, setCreatingNote] = useState(false);

  // Inline rename (2026-06-10) — replaces the modal prompt, matching NoteRow's editor:
  // the title turns into a text input with ✓/✕ controls. Enter or ✓ accepts, Escape or
  // ✕ cancels. `renameDoneRef` makes accept/cancel idempotent so the input's blur
  // (which fires on unmount) can't double-commit.
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameDoneRef = useRef(false);

  useEffect(() => {
    if (!renaming) return;
    const el = renameInputRef.current;
    if (el !== null) {
      el.focus();
      el.select();
    }
  }, [renaming]);

  function beginRename(): void {
    renameDoneRef.current = false;
    setRenameDraft(notebook.title);
    setRenaming(true);
  }

  function cancelRename(): void {
    if (renameDoneRef.current) return;
    renameDoneRef.current = true;
    setRenaming(false);
  }

  function commitRename(): void {
    if (renameDoneRef.current) return;
    renameDoneRef.current = true;
    setRenaming(false);
    const next = renameDraft.trim();
    if (next.length === 0 || next === notebook.title) return; // empty / unchanged — no write
    renameMutation.mutate({ notebookId: notebook.id, title: next });
  }

  const onNewNoteHere = useCallback(async () => {
    if (creatingNote) return;
    setCreatingNote(true);
    try {
      let title = 'New note 01';
      try {
        const titles = (await noteTitles({ limit: 1000 })).data.items.map((i) => i.title);
        title = nextNewNoteTitle(titles);
      } catch {
        /* keep the default — a title read failure must not block creation */
      }
      const resp = await createNote({ title, body: '', notebook: notebook.id });
      void qc.invalidateQueries({ queryKey: ['notebooks', 'notes'] });
      void qc.invalidateQueries({ queryKey: ['note-titles'] });
      void qc.invalidateQueries({ queryKey: ['editor.tree.all_notes'] });
      expandNotebookAction(notebook.id);
      navigate(toEditor(resp.data.noteId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notes] create failed', err);
    } finally {
      setCreatingNote(false);
    }
  }, [creatingNote, notebook.id, qc, expandNotebookAction, navigate]);

  // Render-time decision:
  //   1. If user explicitly recorded a state, honor it ('expanded' / 'collapsed').
  //   2. Else, auto-expand when this notebook is on the active path.
  //   3. Else, default closed.
  const filtering = filter !== '';
  const expanded = filtering
    ? // Task 2 — while a filter is active, force every notebook open so its
      // notes are fetched and can be matched (the tree is lazy otherwise).
      true
    : explicit === 'expanded'
      ? true
      : explicit === 'collapsed'
        ? false
        : isOnActivePath;

  // Auto-expand records 'expanded' so subsequent refreshes keep the
  // branch open. CRITICAL: this only fires when there's NO explicit
  // record (explicit === undefined). If the user has manually collapsed
  // — explicit === 'collapsed' — we don't override.
  useEffect(() => {
    if (isOnActivePath && explicit === undefined) {
      expandNotebookAction(notebook.id);
    }
  }, [isOnActivePath, explicit, notebook.id, expandNotebookAction]);

  // Notes in this notebook — only fetched when expanded (gated via `enabled`).
  const notesQuery = useNotebookNotesQuery(notebook.id, expanded);

  // Drag-and-drop: a folder row is BOTH draggable (to move the folder) and a drop target (drop a
  // note/folder onto it to move it inside). dnd-kit hands back two node refs; merge them onto the
  // single row element. The pointer-distance sensor keeps a plain click navigating.
  const drag = useDraggable({
    id: `nb:${notebook.id}`,
    data: { type: 'notebook', id: notebook.id, parentId: notebook.parentId },
  });
  const drop = useDroppable({
    id: `drop:${notebook.id}`,
    data: { type: 'notebook', id: notebook.id },
  });
  const setRowRef = useCallback(
    (el: HTMLElement | null) => {
      drag.setNodeRef(el);
      drop.setNodeRef(el);
    },
    [drag.setNodeRef, drop.setNodeRef],
  );

  function onRowClick(): void {
    // Clicking the notebook row sets the notebook in the URL AND expands the node so
    // the user immediately sees its notes (the standalone behaviour). Records the
    // expansion to localStorage so it persists across refresh.
    expandNotebookAction(notebook.id);
    navigate(`${ROUTES.editor}?notebookId=${encodeURIComponent(notebook.id)}`);
  }
  function onToggleClick(e: React.MouseEvent): void {
    e.stopPropagation();
    if (expanded) {
      collapseNotebookAction(notebook.id);
    } else {
      expandNotebookAction(notebook.id);
    }
  }

  // Task 2 — apply the title filter to this notebook's own notes, then decide
  // whether this notebook row is visible at all. A notebook stays visible when:
  //   - no filter is active, OR
  //   - its own title matches, OR
  //   - it has at least one matching note, OR
  //   - it may contain a deeper match (hasChildren) — keeps ancestors of nested
  //     matches reachable. Empty branches over-render slightly, which is the
  //     sensible/robust trade vs. a full lazy descendant pre-scan.
  const sortedNotes = sortByMode(notesQuery.data?.items ?? [], sortMode);
  const visibleNotes = filtering
    ? sortedNotes.filter((n) => titleMatches(n.title, filter))
    : sortedNotes;
  const selfMatches = titleMatches(notebook.title, filter);
  const nodeVisible =
    !filtering || selfMatches || visibleNotes.length > 0 || notebook.hasChildren;
  if (!nodeVisible) return <></>;

  return (
    <li className="notebooks-pane__node">
      <div
        ref={setRowRef}
        {...drag.attributes}
        {...drag.listeners}
        className={
          'notebooks-pane__row ed-nb-row' +
          (isActive ? ' notebooks-pane__row--active active' : '') +
          (drop.isOver ? ' nb-drop-over' : '') +
          (drag.isDragging ? ' nb-dragging' : '')
        }
        style={{
          paddingLeft: depth * 14 + 8,
          ...(drag.transform ? { transform: CSS.Translate.toString(drag.transform) } : {}),
        }}
        onClick={renaming ? undefined : onRowClick}
        // Double-click a folder to rename it inline (matches the row menu's Rename).
        onDoubleClick={
          renaming
            ? undefined
            : (e) => {
                e.preventDefault();
                e.stopPropagation();
                beginRename();
              }
        }
        onContextMenu={ctx.open}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (renaming) return; // the inline rename input owns its own keys
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick();
          }
        }}
        title={notebook.title}
      >
        <button
          className="notebooks-pane__chevron"
          onClick={onToggleClick}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={expanded}
          type="button"
        >
          <Icon name={expanded ? 'chevron_down' : 'chevron_right'} size={14} />
        </button>
        <Icon name="folder" size={14} className="notebooks-pane__icon" />
        {renaming ? (
          <span
            className="ed-nb-rename"
            // Keep clicks/keys inside the editor from reaching the row (navigate /
            // Space-Enter activation / drag) — lets the spacebar type a multi-word name.
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <input
              ref={renameInputRef}
              className="ed-nb-rename__input"
              value={renameDraft}
              placeholder="Untitled notebook"
              aria-label="Rename notebook"
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelRename();
                }
              }}
              onBlur={() => commitRename()}
            />
            <button
              type="button"
              className="ed-nb-rename__btn"
              aria-label="Save name"
              title="Save (Enter)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                commitRename();
              }}
            >
              <Icon name="check" size={12} />
            </button>
            <button
              type="button"
              className="ed-nb-rename__btn"
              aria-label="Cancel rename"
              title="Cancel (Esc)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                cancelRename();
              }}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        ) : (
          <span className="notebooks-pane__title">{notebook.title}</span>
        )}
        {/* 2026-05-30 redesign — count pill on notebook rows. 2026-05-31 update
            (Chris feedback): the pill now shows the DESCENDANT-INCLUSIVE count
            served by the backend (`notebook.noteCount`) so a top-level folder
            reflects everything inside it, not just its direct notes. Renders
            on every notebook row regardless of expand state; hidden when the
            recursive count is 0 to avoid a noisy "0" pill on empty folders.
            NEVER renders on note rows — they get the file-icon treatment. */}
        {notebook.noteCount > 0 && (
          <span className="nb-count">{notebook.noteCount}</span>
        )}
        {!isInbox && (
          <AutorouteFolderDot
            notebookId={notebook.id}
            enabled={notebook.autorouterEnabled}
          />
        )}
        {/* The ⋯ menu treats the Archive like the Inbox: protected — no Rename/Delete. */}
        <NotebookRowMenu notebook={notebook} isInbox={isInbox || isArchive} onRename={beginRename} />
        {nbPromptModal}
        {nbConfirmModal}
        <RowContextMenu
          position={ctx.position}
          onClose={ctx.close}
          items={[
            {
              label: 'New note here',
              onClick: () => void onNewNoteHere(),
            },
            // UX fix #93 — "New subfolder" creates a child notebook here, expanding the
            // parent so the new row is visible immediately. Available on every notebook
            // (including the protected Inbox — the backend refuses to RENAME or DELETE the
            // Inbox but children under it are fine).
            {
              label: 'New subfolder…',
              onClick: () => {
                void (async () => {
                  const title = await nbPrompt({
                    eyebrow: 'New subfolder',
                    title: `Name your subfolder under "${notebook.title}"`,
                    placeholder: 'Untitled subfolder',
                    confirmLabel: 'Create',
                  });
                  if (title === null) return;
                  createMutation.mutate(
                    { title, parentId: notebook.id },
                    {
                      onSuccess: () => {
                        // Auto-expand the parent so the new child row is visible.
                        expandNotebookAction(notebook.id);
                      },
                    },
                  );
                })();
              },
            },
            {
              label: notebookPinned ? 'Unpin notebook' : 'Pin notebook',
              onClick: () =>
                notebookPinned ? unpinNotebook(notebook.id) : pinNotebook(notebook.id),
            },
            // The protected system Inbox + Archive hide Rename/Delete/Archive (the backend
            // refuses rename/move/delete on both too).
            ...((isInbox || isArchive
              ? []
              : [
                  // Inline rename in the row — no modal (2026-06-10).
                  { label: 'Rename', onClick: () => beginRename() },
                  // 2026-06-12 — move this folder into the system Archive (its own bottom
                  // section). Reversible: drag it back out, or reparent via MCP.
                  {
                    label: 'Archive folder',
                    onClick: () => archiveMutation.mutate({ notebookId: notebook.id }),
                  },
                  {
                    label: 'Delete…',
                    danger: true,
                    onClick: () => {
                      void (async () => {
                        const ok = await nbConfirm({
                          eyebrow: 'Delete notebook',
                          title: `Delete "${notebook.title}"?`,
                          message:
                            'Notes inside the notebook are preserved — they stay assigned and can be recovered from the Trash folder at the bottom of the notebook tree.',
                          confirmLabel: 'Delete',
                          cancelLabel: 'Keep',
                          destructive: true,
                        });
                        if (!ok) return;
                        deleteMutation.mutate(notebook.id);
                      })();
                    },
                  },
                ]) as RowContextMenuItem[]),
          ]}
        />
      </div>
      {expanded && (
        <>
          {/* Notes inside this notebook — rendered as leaf rows under the folder. */}
          {notesQuery.isLoading && (
            <div
              className="notebooks-pane__loading"
              style={{ paddingLeft: (depth + 1) * 14 + 12 }}
            >
              <LoadingState variant="inline" label="Loading notes…" />
            </div>
          )}
          {notesQuery.isError && (
            <div
              className="notebooks-pane__error"
              style={{ paddingLeft: (depth + 1) * 14 + 12 }}
            >
              <InlineError
                error={toErrorEnvelope(notesQuery.error)}
                onRetry={() => void notesQuery.refetch()}
              />
            </div>
          )}
          {visibleNotes.map((note) => (
            <NoteRow
              key={note.noteId}
              note={note}
              depth={depth + 1}
              isActive={note.noteId === activeNoteId}
              shouldPulse={note.noteId === pulseNoteId}
            />
          ))}
          {/* Recursive: child NOTEBOOKS under this one. */}
          <NotebookLevel
            parentId={notebook.id}
            depth={depth + 1}
            activeNotebookId={activeNotebookId}
            activeNoteId={activeNoteId}
            pulseNoteId={pulseNoteId}
            activeAncestorIds={activeAncestorIds}
            filter={filter}
          />
        </>
      )}
    </li>
  );
}

interface NoteRowProps {
  note: NoteSummary;
  depth: number;
  isActive: boolean;
  /** 2026-05-18 user story — when true, render the blue-pulse class AND
   *  scroll this row into the centre of its scroll container. Toggled by
   *  the parent's `pulseNoteId` state, which clears 5s after the
   *  activeNoteId transition. */
  shouldPulse?: boolean;
}

function NoteRow({
  note,
  depth,
  isActive,
  shouldPulse = false,
}: NoteRowProps): JSX.Element {
  const navigate = useNavigate();
  // Phase E.4 — right-click context menu (Rename / Delete / Copy markdown).
  // The mutations here go through the existing MCP tool wrappers; no new
  // backend surface needed.
  const ctx = useContextMenuPosition();
  const qc = useQueryClient();
  // Phase E.4b — branded confirm modal for delete. Rename is now an inline editor (below),
  // not a modal.
  const { modal: noteConfirmModal, confirm: noteConfirm } = useConfirmModal();
  // Task 5 — pin / unpin this note from the row context menu.
  const isPinned = useUiStore((s) => s.isPinned);
  const pinNote = useUiStore((s) => s.pinNote);
  const unpinNote = useUiStore((s) => s.unpinNote);
  const notePinned = isPinned(note.noteId);
  // facelift-v3 guide 6.3 — read-only details modal (opened from the menu).
  const [detailsOpen, setDetailsOpen] = useState(false);
  const noteTitleDisplay = note.title || 'Untitled note';

  // Inline rename (replaces the old modal prompt): the title turns into a text input with
  // ✓/✕ controls. Enter or ✓ accepts, Escape or ✕ cancels. `renameDoneRef` makes
  // accept/cancel idempotent so the input's blur (which fires on unmount) can't double-commit.
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameDoneRef = useRef(false);

  useEffect(() => {
    if (!renaming) return;
    const el = renameInputRef.current;
    if (el !== null) {
      el.focus();
      el.select();
    }
  }, [renaming]);

  // Drag-and-drop: a note is draggable (drop it onto a folder to move it there). It's not itself
  // a drop target. The pointer-distance sensor keeps a plain click navigating to the note.
  const drag = useDraggable({ id: `note:${note.noteId}`, data: { type: 'note', id: note.noteId } });

  function onClick(): void {
    navigate(toEditor(note.noteId));
  }

  function beginRename(): void {
    renameDoneRef.current = false;
    setRenameDraft(note.title || '');
    setRenaming(true);
  }

  function cancelRename(): void {
    if (renameDoneRef.current) return;
    renameDoneRef.current = true;
    setRenaming(false);
  }

  async function commitRename(): Promise<void> {
    if (renameDoneRef.current) return;
    renameDoneRef.current = true;
    setRenaming(false);
    const next = renameDraft.trim();
    if (next.length === 0 || next === note.title) return; // empty / unchanged — no write
    try {
      await updateNote({ id: note.noteId, title: next });
      // Refresh anything that displays the title.
      void qc.invalidateQueries({ queryKey: ['notebooks', 'notes'] });
      void qc.invalidateQueries({ queryKey: ['note-titles'] });
      void qc.invalidateQueries({ queryKey: ['note', note.noteId] });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notes] rename failed', err);
    }
  }

  async function onDelete(): Promise<void> {
    const ok = await noteConfirm({
      eyebrow: 'Delete note',
      title: `Delete "${noteTitleDisplay}"?`,
      message:
        'The note is moved to the Trash folder at the bottom of the notebook tree. Restore it from there — or delete it permanently.',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiDeleteNote({ id: note.noteId });
      void qc.invalidateQueries({ queryKey: ['notebooks', 'notes'] });
      void qc.invalidateQueries({ queryKey: ['note-titles'] });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notes] delete failed', err);
    }
  }

  async function onCopyMarkdown(): Promise<void> {
    // Pulls the raw markdown body via the same `get_note` tool the editor
    // uses. Doesn't rely on the editor being open — works from any tree
    // context (search hit, sidebar, etc.). `getNote` resolves with
    // `{ data: NoteView, meta }`; rejections surface as thrown errors.
    try {
      const resp = await getNote({ id: note.noteId });
      const view = resp.data;
      const body = view.body ?? '';
      const title = view.note?.title ?? note.title ?? '';
      // Prepend the title as a markdown H1 so the clipboard has a complete
      // standalone document (the body field on its own is body-only).
      const md = title ? `# ${title}\n\n${body}` : body;
      await navigator.clipboard.writeText(md);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[notes] copy markdown failed', err);
    }
  }

  // The scroll-into-view on pulse is centralised at <NotebooksTreePane>
  // (one effect, queries by `[data-note-id]`). Per-row refs would multiply
  // unnecessarily across the tree, and a single query handles both the
  // tree-rendered row and the (possibly duplicate) row in PinnedSection.
  return (
    <>
      {noteConfirmModal}
      {detailsOpen && (
        <NoteDetailsModal
          noteId={note.noteId}
          title={noteTitleDisplay}
          onClose={() => setDetailsOpen(false)}
        />
      )}
      <div
        ref={drag.setNodeRef}
        {...drag.attributes}
        {...drag.listeners}
        data-note-id={note.noteId}
        className={
          'notebooks-pane__row ed-nb-row ed-nb-file' +
          (isActive ? ' active' : '') +
          (shouldPulse ? ' notebooks-pane__row--just-selected' : '') +
          (drag.isDragging ? ' nb-dragging' : '')
        }
        style={{
          paddingLeft: depth * 14 + 28,
          ...(drag.transform ? { transform: CSS.Translate.toString(drag.transform) } : {}),
        }}
        onClick={renaming ? undefined : onClick}
        // Double-click a note to rename it inline (matches the row menu's Rename).
        onDoubleClick={
          renaming
            ? undefined
            : (e) => {
                e.preventDefault();
                e.stopPropagation();
                beginRename();
              }
        }
        // Prefetch the note body on hover so the click opens the editor instantly
        // (Phase 3 perf). prefetchQuery dedupes + respects staleTime, so this is cheap.
        onMouseEnter={() => prefetchNote(qc, note.noteId)}
        onContextMenu={ctx.open}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (renaming) return; // the inline rename input owns its own keys
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        title={noteTitleDisplay}
      >
        <Icon name="edit" size={12} className="notebooks-pane__icon" />
        {renaming ? (
          <span
            className="ed-nb-rename"
            // Keep clicks/keys inside the editor from reaching the row (navigate / Space-Enter
            // activation / drag) — this is what lets the spacebar type a multi-word title.
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <input
              ref={renameInputRef}
              className="ed-nb-rename__input"
              value={renameDraft}
              placeholder="Untitled note"
              aria-label="Rename note"
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void commitRename();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelRename();
                }
              }}
              onBlur={() => void commitRename()}
            />
            <button
              type="button"
              className="ed-nb-rename__btn"
              aria-label="Save name"
              title="Save (Enter)"
              // preventDefault on mousedown so the input doesn't blur before the click runs.
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                void commitRename();
              }}
            >
              <Icon name="check" size={12} />
            </button>
            <button
              type="button"
              className="ed-nb-rename__btn"
              aria-label="Cancel rename"
              title="Cancel (Esc)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                cancelRename();
              }}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        ) : (
          <>
            {/* Unintrusive MM/DD created date, left of the title (Chris, 2026-06-23). */}
            <span className="notebooks-pane__note-date" title={`Created ${noteTitleDisplay}`}>
              {formatNoteRowDate(note.createdAt)}
            </span>
            <span className="notebooks-pane__title">{noteTitleDisplay}</span>
            <AutoroutedBadge noteId={note.noteId} />
          </>
        )}
        <RowContextMenu
          position={ctx.position}
          onClose={ctx.close}
          items={[
            {
              label: notePinned ? 'Unpin' : 'Pin',
              onClick: () => (notePinned ? unpinNote(note.noteId) : pinNote(note.noteId)),
            },
            { label: 'Rename', onClick: () => beginRename() },
            { label: 'Details…', onClick: () => setDetailsOpen(true) },
            { label: 'Copy as Markdown', onClick: () => void onCopyMarkdown() },
            { label: 'Delete…', danger: true, onClick: () => void onDelete() },
          ]}
        />
      </div>
    </>
  );
}
